import { useEffect, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import PropTypes from 'prop-types';

import { useCustomColumns } from '#customColumnsFeature/models/queries';
import TextInput from '#globalComponents/Forms/TextInput';

import { postMailingCampaignsKeys } from '../../models/campaigns/keys';
import { useUpdatePostMailingCampaign } from '../../models/campaigns/mutations';

/**
 * Recipients excluded from a letter campaign because a custom column used in the design has no
 * value for them; a campaign fallback per column brings them back (and re-prices the quote).
 */
const SkippedRecipientsNotice = ({ campaignId, skipped, customVariableFallbacks }) => {
	const queryClient = useQueryClient();
	const { data: columns = [] } = useCustomColumns();
	const updateCampaign = useUpdatePostMailingCampaign();
	const [fallbacks, setFallbacks] = useState({});

	useEffect(() => {
		setFallbacks(customVariableFallbacks ?? {});
	}, [customVariableFallbacks]);

	const keys = Object.keys(skipped?.byKey ?? {});
	if (!skipped?.count && Object.keys(fallbacks).length === 0) return null;

	const nameOf = (key) => columns.find((column) => column.key === key)?.name ?? key;
	const shownKeys = [...new Set([...keys, ...Object.keys(customVariableFallbacks ?? {})])];

	const save = async () => {
		const cleaned = Object.fromEntries(Object.entries(fallbacks).filter(([, text]) => text?.trim()));
		await updateCampaign.mutateAsync({
			id: campaignId,
			updates: { customVariableFallbacks: Object.keys(cleaned).length > 0 ? cleaned : null },
		});
		queryClient.invalidateQueries({ queryKey: postMailingCampaignsKeys.costEstimate(campaignId) });
	};

	return (
		<div className="rounded-card border border-caution-500/40 bg-caution-200 p-3 space-y-2">
			<p className="text-xs text-caution-700">
				{skipped?.count
					? `${skipped.count.toLocaleString('de-DE')} Empfänger werden nicht angeschrieben, weil ein Spaltenwert fehlt. Mit einem Ersatzwert werden sie wieder einbezogen.`
					: 'Ersatzwerte dieser Kampagne:'}
			</p>
			{shownKeys.map((key) => (
				<div key={key} className="flex items-center gap-2">
					<span className="text-xs text-text w-32 truncate" title={nameOf(key)}>
						{nameOf(key)}
						{skipped?.byKey?.[key] ? ` (${skipped.byKey[key]})` : ''}
					</span>
					<TextInput
						value={fallbacks[key] ?? ''}
						onChange={(e) => setFallbacks((prev) => ({ ...prev, [key]: e.target.value }))}
						onBlur={save}
						placeholder="Ersatzwert"
					/>
				</div>
			))}
		</div>
	);
};

SkippedRecipientsNotice.propTypes = {
	campaignId: PropTypes.string.isRequired,
	skipped: PropTypes.shape({ count: PropTypes.number, byKey: PropTypes.object }),
	customVariableFallbacks: PropTypes.object,
};

export default SkippedRecipientsNotice;
